import { Button } from "@/core/components/ui/button";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { formatDateRange } from "@/core/utils/date";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import { RequestApprovalRecipientRow } from "./request-approval-recipient-row";
import type { ISheetApprovalRequestState } from "./types";
import {
  getSelectableRecipients,
  hasAnyApproval,
  resolveUnitApprovals,
} from "./utils";

interface RequestApprovalDialogBodyProps {
  state: ISheetApprovalRequestState;
  approvalStatus: IPayrollSheetApprovalStatus | null | undefined;
  selectedIds: string[];
  onToggle: (userId: string) => void;
  onToggleAll: () => void;
}

/** Recipient selection and approval context inside the request dialog. */
export const RequestApprovalDialogBody = ({
  state,
  approvalStatus,
  selectedIds,
  onToggle,
  onToggleAll,
}: RequestApprovalDialogBodyProps) => {
  const selectable = getSelectableRecipients(state);
  const units = resolveUnitApprovals(state.approval_units, approvalStatus);
  const showStatus = hasAnyApproval(units);
  const allSelected = selectedIds.length === selectable.length;

  return (
    <Stack
      className="min-h-0 overflow-hidden px-6 pb-2 pt-0"
      gap="md"
      fullWidth
    >
      <Typography variant="bodyW2" textColor="secondary">
        Each person you pick gets one email for{" "}
        <strong className="font-semibold">
          {formatDateRange(state.period.start_date, state.period.end_date)}
        </strong>
        , covering only their own sheets, with a link to sign in and approve.
      </Typography>

      <Stack
        gap="none"
        fullWidth
        className="min-h-0 flex-1 overflow-hidden rounded-lg border border-stroke-tertiary"
      >
        <Stack
          direction="row"
          align="center"
          justify="between"
          gap="sm"
          fullWidth
          className="shrink-0 border-b border-stroke-tertiary bg-surface-secondary px-4 py-2"
        >
          <Typography variant="bodyW3" textColor="tertiary">
            {selectedIds.length} of {selectable.length} selected
          </Typography>
          <Button
            variant="link"
            size="sm"
            onClick={onToggleAll}
            className="h-auto px-0 py-0 text-sm font-medium"
          >
            {allSelected ? "Clear all" : "Select all"}
          </Button>
        </Stack>

        <ScrollArea type="auto" className="min-h-0 w-full flex-1">
          <Stack
            gap="none"
            fullWidth
            className="divide-y divide-stroke-tertiary"
          >
            {selectable.map((recipient) => (
              <RequestApprovalRecipientRow
                key={recipient.user_id}
                recipient={recipient}
                units={units}
                showStatus={showStatus}
                checked={selectedIds.includes(recipient.user_id)}
                onToggle={onToggle}
              />
            ))}
          </Stack>
        </ScrollArea>
      </Stack>
    </Stack>
  );
};
